import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useProfile } from '../../hooks/useProfile';
import ExitImpersonationConfirmModal from '../modals/ExitImpersonationConfirmModal/ExitImpersonationConfirmModal';
import './Header.css';

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, originalUser, stopImpersonating } = useAuth();
  const { data: profile } = useProfile();
  const [isExitImpersonationModalOpen, setIsExitImpersonationModalOpen] = useState(false);
  
  // Используем аватарку из профиля, если она есть, иначе из контекста
  const avatar = profile?.avatar || user?.avatar || '/avatar.png';
  
  // Проверяем, находимся ли мы под другим пользователем
  const isImpersonating = !!originalUser;
  
  // Проверяем права доступа к администрированию
  // Явно проверяем на true/1, так как значения могут быть 0/1 из БД или boolean
  const hasAdminAccess = Boolean(profile?.is_system_admin) || Boolean(profile?.is_user_manager);
  
  // Функция для проверки разрешения по slug
  const hasPermission = (permissionSlug: string): boolean => {
    // Администраторы имеют все разрешения
    if (hasAdminAccess && Boolean(profile?.is_system_admin)) {
      return true;
    }
    
    // Проверяем разрешения из профиля
    if (!profile?.permissions) {
      return false;
    }
    
    // Ищем разрешение во всех категориях
    for (const categoryPermissions of Object.values(profile.permissions)) {
      if (Array.isArray(categoryPermissions)) {
        const hasPerm = categoryPermissions.some((perm: any) => perm.slug === permissionSlug);
        if (hasPerm) {
          return true;
        }
      }
    }
    
    return false;
  };
  
  // Проверяем доступ к разделам через разрешения глобального доступа
  const hasClientsAccess = hasPermission('global_access_clients');
  const hasProjectsAccess = hasPermission('global_access_projects');
  const hasCasesAccess = hasPermission('global_access_cases');
  const hasTasksAccess = hasPermission('global_access_work_tasks');
  const hasCalendarAccess = hasPermission('global_access_calendar');
  const hasReportsAccess = hasPermission('global_access_reports');
  
  const navItems = [
    ...(hasClientsAccess ? [{ id: 'clients', label: 'Клиенты', icon: '/icons/navigation/client.svg', path: '/clients' }] : []),
    ...(hasProjectsAccess ? [{ id: 'projects', label: 'Проекты', icon: '/icons/navigation/project.svg', path: '/projects' }] : []),
    ...(hasCasesAccess ? [{ id: 'cases', label: 'Дела', icon: '/icons/navigation/dela.svg', path: '/cases' }] : []),
    ...(hasTasksAccess ? [{ id: 'tasks', label: 'Задачи', icon: '/icons/navigation/zadachi.svg', path: '/tasks' }] : []),
    ...(hasCalendarAccess ? [{ id: 'calendar', label: 'Календарь', icon: '/icons/navigation/calendar.svg', path: '/calendar' }] : []),
    ...(hasReportsAccess ? [{ id: 'reports', label: 'Отчеты', icon: '/icons/navigation/reports.svg', path: '/reports' }] : []),
    ...(hasAdminAccess ? [{ id: 'admin', label: 'Администрирование', icon: '/icons/navigation/admin.svg', path: '/admin' }] : []),
  ];

  const isActive = (path: string) => {
    const { pathname } = location;
    if (path === '/dashboard') {
      return pathname === '/' || pathname === '/dashboard';
    }
    return pathname === path || pathname.startsWith(`${path}/`);
  };

  return (
    <div className="dashboard-header">
      <Link to="/dashboard" className="dashboard-logo" style={{ cursor: 'pointer', textDecoration: 'none' }}>
        <img src="/logo.svg" alt="A2K CRM" className="logo-image" />
      </Link>
      <div className="dashboard-nav">
        {navItems.map((item) => (
          <Link
            key={item.id}
            to={item.path}
            className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
          >
            <span className="nav-icon">
              <img src={item.icon} alt={item.label} />
            </span>
            {item.label}
          </Link>
        ))}
      </div>
      <div className="dashboard-user">
        {isImpersonating && (
          <button
            type="button"
            className="dashboard-exit-impersonation-btn"
            onClick={() => setIsExitImpersonationModalOpen(true)}
            title="Выйти обратно в свой аккаунт"
            aria-label="Выйти обратно в свой аккаунт"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        )}
        <div 
          className={`notification-icon ${isActive('/notifications') ? 'active' : ''}`}
          onClick={() => navigate('/notifications')}
          style={{ cursor: 'pointer' }}
        >
          <img src="/icons/header/notification.svg" alt="Уведомления" />
          <span className="notification-badge">21</span>
        </div>
        <div 
          className={`settings-icon ${isActive('/settings') ? 'active' : ''}`}
          onClick={() => navigate('/settings')}
          style={{ cursor: 'pointer' }}
        >
          <img src="/icons/header/settings.svg" alt="Настройки" />
        </div>
        <div 
          className={`user-profile ${isActive('/dashboard') ? 'active' : ''}`}
          onClick={() => navigate('/dashboard')}
          style={{ cursor: 'pointer' }}
        >
          <div className="user-avatar">
            <img src={avatar} alt={user?.name || profile?.name || 'Пользователь'} />
          </div>
          <span className="user-name">{user?.name || 'Пользователь'}</span>
        </div>
      </div>
      <ExitImpersonationConfirmModal
        isOpen={isExitImpersonationModalOpen}
        onClose={() => setIsExitImpersonationModalOpen(false)}
        onConfirm={async () => {
          try {
            await stopImpersonating();
            window.location.href = '/dashboard';
          } catch (error) {
            console.error('Ошибка при возврате к своему аккаунту:', error);
            alert('Не удалось вернуться к своему аккаунту');
          }
        }}
        originalUserName={originalUser?.name || 'ваш аккаунт'}
      />
    </div>
  );
};

export default Header;

